import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Modal,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  AppBar,
  Toolbar,
  Pagination,
  Badge,
  CircularProgress,
  styled,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Chip,
  List,
  ListItem,
  Divider,
  Avatar,
  Card,
  CardContent,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
  MoreVert as MoreVertIcon
} from "@mui/icons-material";
import LeftToolbar from "./LeftToolbar";
import NotificationSnackbar from "./NotificationBar";
import { alpha } from "@mui/material/styles";
import Footer from "./Footer";
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import headerImg from "./assets/logoMtfr.png";
import "./Ronnie";
// data will be fetched from APIs
const api = axios.create({ withCredentials: true });
const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create(["margin"], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
  overflowX: "auto",
  background: "white",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: alpha(theme.palette.primary.light, 0.03),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.08),
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[1],
  },
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&:first-of-type": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const CommentBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  borderRadius: '8px',
  border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
}));

const UserFeedBacks = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState("User Feedbacks");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  const itemsPerPage = 5;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedUserComments, setSelectedUserComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const normalizeApiArray = (payload, key) => {
    if (!payload) {
      return [];
    }
    const source =
      key && !Array.isArray(payload) ? payload[key] ?? payload : payload;
    if (!source) {
      return [];
    }
    if (Array.isArray(source)) {
      if (source.length > 0 && Array.isArray(source[0])) {
        return source[0];
      }
      return source;
    }
    if (Array.isArray(source.recordset)) {
      return source.recordset;
    }
    return [];
  };

  const fetchSurveys = async () => {
    try {
      const { data } = await api.get("/api/support/surveys");
      if (!data) {
        throw new Error("No survey data returned from server");
      }
      return normalizeApiArray(data, "surveys");
    } catch (error) {
      console.error("Error fetching surveys:", error);
      throw error;
    }
  };

  const fetchSurveyResponses = async (surveyId) => {
    try {
      const { data } = await api.get(
        `/api/support/survey-responses/${surveyId}`,
        { timeout: 120000 }
      );
      const normalized = normalizeApiArray(data);
      console.log("Survey responses normalized:", normalized);
      return normalized;
    } catch (error) {
      console.error("Error fetching survey responses:", error);
      throw error;
    }
  };

  const fetchSurveyComments = async (userId) => {
    try {
      const { data } = await api.get(`/api/support/survey-comments/${userId}`);
      return normalizeApiArray(data);
    } catch (error) {
      console.error("Error fetching survey comments:", error);
      return [];
    }
  };

  const fetchSurveyQuestionsMap = async (surveyId) => {
    try {
      const { data } = await api.get(
        `/api/support/survey-questions/${surveyId}`
      );
      const rows = normalizeApiArray(data);
      console.log(`Raw questions data for survey ${surveyId}:`, rows);

      const map = {};
      rows.forEach((row) => {
        // Try all possible field names for question ID
        const qid = row.QuestionId ?? row.questionId ?? row.id ?? row.ID;
        // Try all possible field names for question text
        const qtext = row.questionText ?? row.QuestionText ?? row.text ?? row.Text;

        const key = qid != null ? String(qid) : null;
        if (key && qtext && !map[key]) {
          map[key] = qtext;
          console.log(`Mapped question ${key} to: ${qtext}`);
        }
      });

      console.log(`Processed question map for survey ${surveyId}:`, map);
      return map;
    } catch (error) {
      console.error("Error fetching survey questions:", error);
      return {};
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const surveys = await fetchSurveys();
        console.log("Fetched surveys:", surveys);

        // ✅ NO LIMITS — process all surveys
        const surveysToProcess = Array.isArray(surveys) ? surveys : [];

        const uniqueUserIds = new Set();
        const surveyDataCache = {};

        for (const survey of surveysToProcess) {
          const surveyId = survey.surveyId || survey.SurveyId || survey.id;

          if (!surveyId) {
            console.warn("Survey missing ID:", survey);
            continue;
          }

          try {
            // ✅ NO LIMITS — keep all responses
            const responses = await fetchSurveyResponses(surveyId);
            const allResponses = Array.isArray(responses) ? responses : [];

            const questionMap = await fetchSurveyQuestionsMap(surveyId);

            surveyDataCache[surveyId] = {
              responses: allResponses,
              questionMap,
              surveyTitle:
                survey.surveyTitle ||
                survey.SurveyTitle ||
                survey.title ||
                "Unknown Survey",
              surveyModule:
                survey.moduleName ||
                survey.ModuleName ||
                survey.module ||
                null,
            };

            for (const resp of allResponses) {
              const userId =
                resp.userId ||
                resp.UserId ||
                resp.respondentId ||
                resp.RespondentId;

              if (!userId) {
                console.warn("Response missing userId:", resp);
                continue;
              }

              uniqueUserIds.add(userId);
            }
          } catch (innerError) {
            console.warn(
              `Failed to collect user ids or survey data for survey ${surveyId}:`,
              innerError
            );
          }
        }

        console.log(`Need to fetch ${uniqueUserIds.size} unique users.`);

        let hadUserFetchFailure = false;

        async function fetchUsersInBatches(userIds, batchSize = 20) {
          const ids = Array.from(userIds);

          let existingCache = {};
          try {
            if (typeof window !== "undefined") {
              const raw = window.sessionStorage.getItem(
                "supportUserNameCache"
              );
              if (raw) {
                existingCache = JSON.parse(raw);
              }
            }
          } catch (cacheError) {
            console.warn("Failed to read user name cache:", cacheError);
          }

          const userMap = { ...existingCache };
          const idsToFetch = ids.filter((id) => !existingCache[id]);

          for (let i = 0; i < idsToFetch.length; i += batchSize) {
            const batch = idsToFetch.slice(i, i + batchSize);

            const promises = batch.map((id) =>
              api
                .get(`/api/support/user/${encodeURIComponent(id)}`, {
                  timeout: 8000,
                  withCredentials: true,
                })
                .then((res) => {
                  const fullName =
                    res.data?.fullName ||
                    [res.data?.firstName, res.data?.lastName]
                      .filter(Boolean)
                      .join(" ") ||
                    `User ${id}`;
                  return { id, name: fullName };
                })
                .catch((err) => {
                  console.warn(`Failed to fetch user ${id}:`, err);
                  hadUserFetchFailure = true;
                  return { id, name: `User ${id}` };
                })
            );

            const results = await Promise.allSettled(promises);

            results.forEach((result) => {
              if (result.status === "fulfilled") {
                userMap[result.value.id] = result.value.name;
              }
            });
          }

          try {
            if (typeof window !== "undefined") {
              window.sessionStorage.setItem(
                "supportUserNameCache",
                JSON.stringify(userMap)
              );
            }
          } catch (cacheError) {
            console.warn("Failed to write user name cache:", cacheError);
          }

          return userMap;
        }

        const userMap = await fetchUsersInBatches(uniqueUserIds, 20);

        const usersMap = {};

        for (const survey of surveysToProcess) {
          const surveyId = survey.surveyId || survey.SurveyId || survey.id;
          if (!surveyId) continue;

          const cachedSurvey = surveyDataCache[surveyId];
          if (!cachedSurvey) continue;

          const { responses, questionMap, surveyTitle, surveyModule } =
            cachedSurvey;

          for (const resp of responses) {
            const userId =
              resp.userId ||
              resp.UserId ||
              resp.respondentId ||
              resp.RespondentId;

            if (!userId) continue;

            if (!usersMap[userId]) {
              usersMap[userId] = {
                id: userId,
                userId,
                name: userMap[userId] || `User ${userId}`,
                avatar: null,
                module:
                  resp.moduleName ||
                  resp.ModuleName ||
                  surveyModule ||
                  "Unknown Module",
                surveyTitle,
                surveyId,
                responses: [],
              };
            }

            const qIdRaw =
              resp.questionId ?? resp.QuestionId ?? resp.QuestionID;
            const qId = qIdRaw != null ? String(qIdRaw) : undefined;

            usersMap[userId].responses.push({
              QuestionID: qId,
              ResponseText:
                resp.responseText || resp.ResponseText || "",
              Comment: resp.comment || resp.Comment,
              CreatedOn: resp.createdOn || resp.CreatedOn,
              questionText:
                (qId && questionMap[qId]) ||
                resp.questionText ||
                resp.QuestionText ||
                (qId ? `Question ${qId}` : "Question"),
            });
          }
        }

        const usersArray = Object.values(usersMap);
        setUsers(usersArray);

        const categoriesSet = new Set(usersArray.map((u) => u.module));
        setCategories(
          Array.from(categoriesSet).map((name, index) => ({
            id: (index + 1).toString(),
            categoryName: name,
          }))
        );

        if (hadUserFetchFailure) {
          handleSnackbarOpen(
            "Some user names could not be loaded",
            "warning"
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
        handleSnackbarOpen("Failed to load user data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (selectedModule === "" || user.module === selectedModule)
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const handleOpenModal = (type, user) => {
    setSelectedUser(user);
    console.log("Selected user:", user);
    setModalType(type);
    if (type === "view" && user?.userId) {
      setLoadingComments(true);
      fetchSurveyComments(user.userId).then((comments) => {
        setSelectedUserComments(Array.isArray(comments) ? comments : []);
        setLoadingComments(false);
      });
    }
  };




  const handleCloseModal = () => {
    setModalType(null);
    setSelectedUser(null);
    setSelectedUserComments([]);
  };


  const handleExcelDownloadAll = async () => {
    try {
      const rows = [];

      const userIds = Array.from(
        new Set(filteredUsers.map((u) => u.userId).filter(Boolean))
      );

      const commentsMap = {};

      const batchSize = 20;
      for (let i = 0; i < userIds.length; i += batchSize) {
        const batch = userIds.slice(i, i + batchSize);
        const promises = batch.map((id) =>
          fetchSurveyComments(id)
            .then((comments) => ({ id, comments }))
            .catch((err) => {
              console.warn(`Failed to fetch comments for user ${id}:`, err);
              return { id, comments: [] };
            })
        );

        const results = await Promise.all(promises);
        results.forEach((result) => {
          commentsMap[result.id] = Array.isArray(result.comments)
            ? result.comments
            : [];
        });
      }

      for (const u of filteredUsers) {
        const comments = commentsMap[u.userId] || [];

        const userComment =
          (comments.length > 0 ? comments[0].comment : null) ||
          u.Comment ||
          "No comment provided";

        const row = {
          "User Name": u.name,
          "User ID": u.userId,
          "Survey ID": u.surveyId,
          Module: u.module,
          "Survey Title": u.surveyTitle,
        };

        (u.responses || []).forEach((r, index) => {
          row[`Q${index + 1}: ${r.questionText}`] =
            r.ResponseText || "No response";
        });

        row["Comment"] = userComment;

        rows.push(row);
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "All_Responses");
      XLSX.writeFile(workbook, `all_users_responses.xlsx`);
      handleSnackbarOpen(
        "Excel file (all users) downloaded successfully",
        "success"
      );
    } catch (error) {
      console.error("Error generating Excel:", error);
    }
  };




  // Updated handlePDFDownloadAll function with proper header image alignment
  const handlePDFDownloadAll = async () => {
    try {
      // === Tunables ===
      const MIN_Q_WIDTH = 80;
      const MIN_COMMENT_WIDTH = 260;
      const MARGINS = { left: 50, right: 40, top: 200 };  // Increased top margin
      // Collect max question count
      let maxQuestions = 0;
      filteredUsers.forEach(u => {
        maxQuestions = Math.max(maxQuestions, (u.responses || []).length);
      });
      // If many questions, switch to A3 for more horizontal space
      const wideFormat = maxQuestions > 8 ? "a3" : "a4";
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: wideFormat,
      });
      // === Add Image Header ===
      const pageWidth = doc.internal.pageSize.getWidth();
      const imgWidth = 300;
      const imgHeight = 120;
      const centerX = (pageWidth - imgWidth) / 2;
      const pageHeight = doc.internal.pageSize.getHeight();
      const tableHeight = 40 + (12 * 30);
      const centerY = (pageHeight - tableHeight) / 2;
      doc.addImage(
        headerImg,
        "PNG",
        centerX,
        25,
        imgWidth,
        imgHeight
      );


      // Add modern header text with Ronnie font
      doc.setFontSize(20);
      doc.setTextColor(45, 52, 75);  // Dark blue-gray
      doc.setFont("Ronnie", "bold"); // use Ronnie here
      doc.text("SURVEY RESPONSE REPORT", pageWidth / 2, 25 + imgHeight + 30, { align: "center" });

      doc.setDrawColor(3, 169, 244);  // Bright blue
      doc.setLineWidth(2);
      doc.line(centerX, 25 + imgHeight + 40, centerX + imgWidth, 25 + imgHeight + 40);

      // Title under the image
      doc.setFontSize(14);
      doc.setTextColor(100, 100, 100);

      // === Pre-normalize all user rows ===
      const usersData = [];

      const userIds = Array.from(
        new Set(filteredUsers.map((u) => u.userId).filter(Boolean))
      );

      const commentsMap = {};
      const batchSizeComments = 20;
      for (let i = 0; i < userIds.length; i += batchSizeComments) {
        const batch = userIds.slice(i, i + batchSizeComments);
        const promises = batch.map((id) =>
          fetchSurveyComments(id)
            .then((comments) => ({ id, comments }))
            .catch((err) => {
              console.warn(`Failed to fetch comments for user ${id}:`, err);
              return { id, comments: [] };
            })
        );

        const results = await Promise.all(promises);
        results.forEach((result) => {
          commentsMap[result.id] = Array.isArray(result.comments)
            ? result.comments
            : [];
        });
      }

      for (const u of filteredUsers) {
        const comments = commentsMap[u.userId] || [];
        const commentText =
          (comments.length
            ? comments.map((c) => c?.comment).filter(Boolean).join(" | ")
            : u.Comment || u.comment) || "No comment provided";

        const questionTexts = (u.responses || []).map(
          r => r?.questionText ?? ""
        );
        const responses = (u.responses || []).map(
          r => r?.ResponseText ?? "No response"
        );

        while (responses.length < maxQuestions) {
          questionTexts.push("");
          responses.push("");
        }
        console.log('User comment field:', commentText);
        usersData.push({
          meta: [
            u.userId ?? "",
            u.name ?? "",
            u.module ?? "",
            u.surveyTitle ?? "",
          ],
          questionTexts,
          responses,
          comment: commentText,
        });
      }
      // Meta headers
      const metaHeaders = [
        "User ID",
        "User Name",
        "Module",
        "Survey Title",
      ];
      const metaWidths = [50, 100, 50, 90, 120];
      const metaCount = metaHeaders.length;
      const available = pageWidth - MARGINS.left - MARGINS.right;
      const sumMeta = metaWidths.reduce((a, b) => a + b, 0);
      const QsPerChunkNoComment = Math.max(
        1,
        Math.floor((available - sumMeta) / MIN_Q_WIDTH)
      );
      const chunks = [];
      let start = 0;
      while (start < maxQuestions) {
        let remaining = maxQuestions - start;
        const canFitWithComment = Math.floor(
          (available - sumMeta - MIN_COMMENT_WIDTH) / MIN_Q_WIDTH
        );

        // Default size based on non-final logic
        let size = Math.max(1, Math.min(remaining, QsPerChunkNoComment));

        // If this chunk reaches or passes the end, mark as final
        if (start + size >= maxQuestions) {
          size = Math.max(1, Math.min(remaining, canFitWithComment));
          chunks.push({ start, end: start + size, isFinal: true });
        } else {
          chunks.push({ start, end: start + size, isFinal: false });
        }

        start += size;
      }


      // Calculate the starting Y position for the table to ensure enough space for the header
      const headerHeight = 15 + imgHeight + 40 + 0; // Image top + image height + title position + margin

      // Render tables
      let firstTable = true;
      for (let i = 0; i < chunks.length; i++) {
        const { start, end, isFinal } = chunks[i];
        const qCount = end - start;
        const qHeaders = Array.from({ length: qCount }, (_, k) => {
          const qIndex = start + k;
          const qText = usersData[0]?.questionTexts[qIndex] || "";
          return `Q${qIndex + 1}: ${qText}`;
        });
        const head = [
          metaHeaders.concat(qHeaders).concat(isFinal ? ["Comment"] : []),
        ];
        const body = usersData.map(u => {
          const row = [...u.meta, ...u.responses.slice(start, end)];
          if (isFinal) row.push(u.comment);
          return row;
        });


        const columnStyles = {};
        metaWidths.forEach((w, idx) => {
          columnStyles[idx] = { cellWidth: w };
        });
        for (let c = 0; c < qCount; c++) {
          columnStyles[metaCount + c] = { cellWidth: MIN_Q_WIDTH };
        }
        if (isFinal) {
          const usedWidth = sumMeta + qCount * MIN_Q_WIDTH;

          const commentWidth = Math.max(
            MIN_COMMENT_WIDTH,
            available - sumMeta - (qCount * MIN_Q_WIDTH)
          );
          columnStyles[metaCount + qCount] = { cellWidth: commentWidth };
        }

        if (!firstTable) {
          doc.addPage();
        }
        firstTable = false;

        autoTable(doc, {
          head,
          body,
          margin: MARGINS,
          styles: {
            fontSize: 9,
            cellPadding: 4,
            valign: "middle",
            overflow: "linebreak",
            halign: "left",
            fillColor: [255, 255, 255],  // White background
            textColor: [45, 52, 75],     // Dark text
            lineColor: [220, 220, 220],  // Light grid lines
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: [3, 168, 245],     // Dark header
            textColor: 255,
            halign: "center",
            valign: "middle",
            fontStyle: "bold",
            fontSize: 10,
          },
          columnStyles,
          tableWidth: "wrap",
          theme: "grid",
          didDrawPage: () => {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            const pageH = doc.internal.pageSize.getHeight();
            doc.text(
              `Generated: ${new Date().toLocaleString()}`,
              pageWidth / 2,
              pageH - 20,
              { align: "center" }
            );
          },
        });
      }

      doc.save("all_users_responses.pdf");
      handleSnackbarOpen(
        "PDF file (all users) downloaded successfully",
        "success"
      );
    } catch (err) {
      console.error("Error generating PDF:", err);
      handleSnackbarOpen("Failed to generate PDF", "error");
    }
  };


  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  // Get unique modules from categories
  const uniqueModules = [...new Set(categories.map(cat => cat.categoryName))];

  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow:
            "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            IPPIS SURVEY PORTAL
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={filteredUsers.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
        onCollapse={setCollapsed}
      />

      <MainContent $collapsed={collapsed}>
        <Toolbar />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <TextField
            label="Search users..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: isMobile ? "100%" : "auto",
              flexWrap: 'wrap'
            }}
          >
            <Select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 200,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="">All Modules</MenuItem>
              {uniqueModules.map((module) => (
                <MenuItem key={module} value={module}>
                  {module}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Global export actions row (separate, below filters) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            mt: -2,
            px: 0,
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleExcelDownloadAll()}
            startIcon={<ExcelIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '12px',
              boxShadow: '0 6px 16px rgba(46,125,50,0.25)',
              bgcolor: '#2e7d32',
              '&:hover': { bgcolor: '#1b5e20' }
            }}
          >
            Download Excel (All)
          </Button>
          <Button
            variant="contained"
            onClick={() => handlePDFDownloadAll()}
            startIcon={<PdfIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '12px',
              boxShadow: '0 6px 16px rgba(211,47,47,0.25)',
              bgcolor: '#d32f2f',
              '&:hover': { bgcolor: '#b71c1c' }
            }}
          >
            Download PDF (All)
          </Button>
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 3,
            mb: 3,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              User Responses
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredUsers.length}{" "}
              {filteredUsers.length === 1 ? "user" : "users"} found
            </Typography>
          </Box>

          <ResponsiveTableContainer component={Paper}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: theme.palette.primary.light,
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    },
                  }}
                >
                  {[
                    "User ID",
                    "User",
                    "Module",
                    "Survey Title",
                    "Actions",
                  ].map((head) => (
                    <ModernTableCell key={head}>{head}</ModernTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <ModernTableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading users...
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <ModernTableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="error">
                        {error || "Failed to load users"}
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                ) : currentItems.length > 0 ? (
                  currentItems.map((user) => (
                    <StyledTableRow key={user.id} hover>
                      <ModernTableCell>
                        {user.userId}
                      </ModernTableCell>

                      <ModernTableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>

                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.name}
                          </Typography>
                        </Box>
                      </ModernTableCell>
                      <ModernTableCell>
                        {isMobile
                          ? `${user.module.substring(0, 15)}...`
                          : user.module.length > 25
                            ? `${user.module.substring(0, 22)}...`
                            : user.module}
                      </ModernTableCell>
                      <ModernTableCell>
                        {isMobile
                          ? `${user.surveyTitle.substring(0, 15)}...`
                          : user.surveyTitle.length > 25
                            ? `${user.surveyTitle.substring(0, 22)}...`
                            : user.surveyTitle}
                      </ModernTableCell>

                      <ModernTableCell>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenModal("view", user)}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                              ),
                            },
                          }}
                        >
                          <VisibilityIcon
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        </IconButton>
                        {/* per-user download removed in favor of global exports */}

                      </ModernTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <ModernTableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1">No users found</Typography>
                    </ModernTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={Math.ceil(filteredUsers.length / itemsPerPage)}
              page={currentPage}
              onChange={(e, value) => setCurrentPage(value)}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Box>
      </MainContent>

      <Footer collapsed={collapsed} />

      {/* View User Responses Modal */}
      <Modal
        open={modalType === "view"}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}

      >
        <Box sx={{
          width: { xs: '85%', sm: '75%', md: '65%', lg: '55%' },
          maxHeight: '65vh',
          overflowY: 'auto',
          bgcolor: 'white',
          borderRadius: '16px',
          p: 3,
          boxShadow: '0 8px 32px rgba(3, 169, 244, 0.2)',
          border: '1px solid #b3e5fc'
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            px: 2,
            py: 1.5,
            bgcolor: '#277ED3',
            color: 'white',
            borderRadius: '12px'
          }}>
            <Typography variant="h5" sx={{
              fontWeight: 700
            }}>
              {selectedUser?.name}<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{
                  color: 'white',
                  fontWeight: 500
                }}>
                  Survey: {selectedUser?.surveyTitle}   <br />
                  Module: {selectedUser?.module}
                </Typography>


              </Box>

            </Typography>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: 'inherit',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>


          {selectedUser?.responses?.length > 0 ? (
            <>
              <List sx={{ maxHeight: '50vh', overflowY: 'auto', pr: 1 }}>
                {selectedUser.responses.map((response, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{
                      display: 'flex',
                      alignItems: 'center', // vertically centers items
                      justifyContent: 'space-around', // left + right spacing
                      px: 0,
                      py: 1.5,
                      flexDirection: 'row', // horizontal layout
                    }}>
                      {/* Question Bubble */}
                      <Box sx={{
                        alignSelf: 'flex-start',
                        maxWidth: '85%',
                        height: 10,
                        mb: 1
                      }}>
                        <Card sx={{
                          bgcolor: '#e3f2fd',
                          border: '1px solid #bbdefb',
                          borderRadius: '18px 18px 18px 4px',
                          boxShadow: '0 2px 8px rgba(3, 169, 244, 0.1)'
                        }}>
                          <CardContent sx={{ py: 0.5, px: 1.5 }}>
                            <Typography variant="body2" sx={{
                              fontWeight: 500,
                              color: '#0d47a1',
                              display: 'flex',
                              alignItems: 'center'
                            }}>

                              {response.questionText || `Question ${response.QuestionID}`}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>

                      {/* Response Bubble */}
                      <Box sx={{
                        alignSelf: 'flex-end',
                        maxWidth: '85%',
                        ml: 4
                      }}>
                        <Card sx={{
                          bgcolor: '#03a9f4',
                          border: '1px solid #4fc3f7',
                          borderRadius: '18px 18px 4px 18px',
                          boxShadow: '0 2px 8px rgba(3, 169, 244, 0.2)'
                        }}>
                          <CardContent sx={{ py: 1, px: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                py: 1,
                              }}
                            >
                              {(() => {
                                const responseText = response.ResponseText || "";
                                if (!responseText) {
                                  return "No response";
                                }
                                if (responseText.includes(",")) {
                                  return (
                                    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                                      {responseText.split(",").map((item, i) => (
                                        <Chip
                                          key={i}
                                          label={item.trim()}
                                          size="small"
                                          sx={{
                                            m: 1,
                                            bgcolor: "white",
                                            color: "#03a9f4",
                                            fontWeight: 500,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  );
                                }
                                return responseText;
                              })()}
                            </Typography>

                          </CardContent>
                        </Card>
                      </Box>
                    </ListItem>

                    {index < selectedUser.responses.length - 1 && (
                      <Divider sx={{
                        my: 1,
                        bgcolor: '#e0e0e0',
                        mx: 6
                      }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
              {/* Overall/User Comment at the bottom of the modal */}
              {(() => {
                const overallComment = selectedUser?.overallComment || selectedUser?.comment || (selectedUser?.responses || []).map(r => r.Comment).filter(Boolean).slice(-1)[0];
                return overallComment ? (
                  <Box sx={{ mt: 2 }}>
                    <Card sx={{
                      bgcolor: '#f0f9ff',
                      border: '1px solid #b3e5fc',
                      borderRadius: '16px',
                      boxShadow: '0 4px 16px rgba(3, 169, 244, 0.15)'
                    }}>
                      <CardContent sx={{ py: 2, px: 2.5 }}>
                        <Typography variant="subtitle2" sx={{
                          color: '#0288d1',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: 0.5
                        }}>
                          User's Comment
                        </Typography>
                        <Typography variant="body2" sx={{
                          color: '#0a2e52',
                          lineHeight: 1.6
                        }}>
                          {overallComment}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                ) : null;
              })()}
              {/* Added: List all fetched survey comments for this user */}
              <Box sx={{ mt: 2 }}>
                {loadingComments ? (
                  <Typography variant="body2" sx={{ color: '#757575' }}>Loading comments...</Typography>
                ) : (
                  selectedUserComments && selectedUserComments.length > 0 && (
                    <Card sx={{ bgcolor: '#fff', border: '1px solid #e0f2fe', borderRadius: '12px' }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ color: '#0288d1', fontWeight: 700, mb: 1 }}>
                          Additional Comments
                        </Typography>
                        <List sx={{ py: 0 }}>
                          {selectedUserComments.map((c, i) => (
                            <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                              <Typography variant="body2" sx={{ color: '#0a2e52' }}>
                                {c.comment || c.Comment}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  )
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ color: '#757575' }}>
                No responses found for this user
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>



      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </MainContainer>
  );
};

export default UserFeedBacks;
